import React from 'react';
import { Download, ArrowRight, Github, Linkedin, Mail, Code2, Database } from 'lucide-react';
import { CONFIG } from '../../config';

/**
 * Hero Section Component
 * 
 * The initial landing screen of the portfolio.
 * Features a call to action, social links, and a visual representation of the developer.
 */
const Hero = ({ setActiveSection }) => {
    return (
        <div className="relative pt-24 md:pt-32 pb-12 md:pb-20 px-4 sm:px-10 max-w-full overflow-hidden">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

                {/* Content */}
                <div className="order-2 lg:order-1 space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-5 duration-700">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300 text-sm font-medium shadow-sm dark:shadow-none hover:shadow-md transition-shadow">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                        </span>
                        Available for New Opportunities
                    </div>

                    <div className="space-y-4">
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.1] break-words hyphens-auto">
                            Dotnet <span className="text-transparent bg-clip-text bg-[linear-gradient(110deg,#9333ea,45%,#c084fc,55%,#9333ea)] bg-[length:200%_100%] animate-shine inline-block">Software</span> <br className="hidden sm:block" />
                            Developer.
                        </h1>
                        <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-xl leading-relaxed font-light">
                            {CONFIG.personal.tagline}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => setActiveSection('projects')}
                            className="px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/25 transition-all flex items-center gap-2 group hover:scale-105"
                        >
                            View Projects
                            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                        </button>

                        <a
                            href={CONFIG.personal.cvUrl}
                            download
                            className="px-8 py-4 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-slate-900 dark:text-white border border-gray-200 dark:border-white/10 rounded-xl font-semibold transition-all flex items-center gap-2 shadow-sm dark:shadow-none hover:scale-105"
                        >
                            <Download size={20} />
                            Resume
                        </a>
                    </div>

                    <div className="pt-8 border-t border-gray-200 dark:border-white/5 flex gap-6">
                        {[
                            { icon: Github, link: CONFIG.personal.github },
                            { icon: Linkedin, link: CONFIG.personal.linkedin },
                            { icon: Mail, link: `mailto:${CONFIG.personal.email}` }
                        ].map(({ icon: Icon, link }, index) => (
                            <a
                                key={index}
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-3 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-500/10 border border-gray-200 dark:border-white/5 rounded-full transition-all shadow-sm dark:shadow-none hover:scale-110 hover:-translate-y-1"
                            >
                                <Icon size={24} />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Visual / Floating Avatar */}
                <div className="order-1 lg:order-2 relative flex justify-center items-center">
                    <div className="absolute inset-0 bg-gradient-to-tr from-purple-500 to-indigo-500 rounded-full blur-[100px] opacity-20 animate-pulse"></div>

                    <div className="relative aspect-square rounded-full overflow-hidden border-[6px] border-white dark:border-slate-800 shadow-[0_0_50px_rgba(168,85,247,0.2)] bg-slate-900/50 backdrop-blur-sm w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] lg:w-[400px] lg:h-[400px] animate-float z-10">
                        <img
                            src={CONFIG.personal.avatar}
                            alt="Hero"
                            className="w-full h-full object-cover opacity-90 scale-105 hover:scale-110 transition-transform duration-700"
                        />
                    </div>
                    
                    {/* Floating Badges */}
                    <div 
                        className="absolute top-[15%] left-[5%] md:left-[10%] bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-gray-200 dark:border-white/10 p-3 sm:p-4 rounded-2xl shadow-xl animate-float z-20"
                        style={{ animationDelay: '1s' }}
                    >
                        <Code2 className="text-purple-600 dark:text-purple-400 w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                    
                    <div 
                        className="absolute bottom-[20%] right-[5%] md:right-[10%] bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border border-gray-200 dark:border-white/10 p-3 sm:p-4 rounded-2xl shadow-xl animate-float z-20"
                        style={{ animationDelay: '2.5s' }}
                    >
                        <Database className="text-indigo-600 dark:text-indigo-400 w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero;
